import { createElement, type ReactNode } from 'react';
import type { EvidenceSpan } from '@scoutaround/script-breakdown-shared';

import type { MyMembership } from '../../lib/api.js';
import { ExecutionView } from '../production/ExecutionView.js';
import { ResourcesView } from '../production/ResourcesView.js';
import { BreakdownTab } from './BreakdownTab.js';

/**
 * THE RIGHT PANEL'S TABS, AS A REGISTRY (integration contract with Production Planning, C7).
 *
 * Breakdown is this workspace's own. Execution and Resources are the Production Planning
 * Engine's merged views, registered behind `features.productionPlan`; while it is off they are
 * not offered, and no placeholder tab stands in for them. A tab's `id` is the address's
 * `?view=` value, so the plan's own links (`?view=execution&scene=`) keep working.
 */
export type ScenePanelTabId = 'breakdown' | 'execution' | 'resources';

export interface ScenePanelContext {
  readonly projectId: string;
  readonly scriptVersionId: string;
  readonly sceneId: string;
  /** The item in view (`?item=`), by its key. */
  readonly selectedItem: string | null;
  readonly selectItem: (key: string | null) => void;
  /** Highlight a quote in the screenplay reader, beside the panel. */
  readonly showEvidence: (span: EvidenceSpan) => void;
}

export interface ScenePanelTab {
  readonly id: ScenePanelTabId;
  readonly label: string;
  readonly isAvailable: (features: MyMembership['features'] | undefined) => boolean;
  readonly render: (ctx: ScenePanelContext) => ReactNode;
}

const planOn = (features: MyMembership['features'] | undefined): boolean => features?.productionPlan === true;

export const SCENE_PANEL_TABS: readonly ScenePanelTab[] = [
  {
    id: 'breakdown',
    label: 'Breakdown',
    isAvailable: () => true,
    render: (ctx) => createElement(BreakdownTab, { key: ctx.sceneId }),
  },
  {
    id: 'execution',
    label: 'Execution',
    isAvailable: planOn,
    render: (ctx) => createElement(ExecutionView, { sceneId: ctx.sceneId }),
  },
  {
    id: 'resources',
    label: 'Resources',
    isAvailable: planOn,
    render: (ctx) => createElement(ResourcesView, { sceneId: ctx.sceneId }),
  },
];

/** The tabs this production offers, in registry order. */
export function availablePanelTabs(features: MyMembership['features'] | undefined): ScenePanelTab[] {
  return SCENE_PANEL_TABS.filter((tab) => tab.isAvailable(features));
}
